// LeadsPage.tsx - Leads list page with filters and CRUD actions
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Stack,
  Group,
  Button,
  TextInput,
  Select,
  Table,
  Badge,
  ActionIcon,
  Tooltip,
  Modal,
  Textarea,
  NumberInput,
  Paper,
  Tabs,
  Menu,
} from '@mantine/core';
import {
  IconPlus,
  IconSearch,
  IconEdit,
  IconTrash,
  IconEye,
  IconFilter,
  IconDownload,
  IconUpload,
} from '@tabler/icons-react';
import { useLeads, useCreateLead, useUpdateLead, useDeleteLead } from '@/hooks/useLeads';
import { LeadImportExport } from '@/components/leads/LeadImportExport';
import { notifications } from '@mantine/notifications';
import { ROUTES } from '@/constants/routes';
import type { Lead, LeadStatus } from '@/types/models/lead.types';

export function LeadsPage() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [sourceFilter, setSourceFilter] = useState<string | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [importExportOpen, setImportExportOpen] = useState(false);

  const createLead = useCreateLead();
  const updateLead = useUpdateLead();
  const deleteLead = useDeleteLead();

  // Build filters
  const filters: any = {};
  if (statusFilter) filters.status = statusFilter;
  if (sourceFilter) filters.source = sourceFilter;

  const { data: leadsResponse, isLoading } = useLeads(filters);
  const leads = leadsResponse?.data || [];

  // Filter leads by search query
  const filteredLeads = leads.filter((lead: Lead) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      lead.customer_name?.toLowerCase().includes(query) ||
      lead.email?.toLowerCase().includes(query) ||
      lead.phone?.includes(query) ||
      lead.car_interest?.toLowerCase().includes(query)
    );
  });

  const statusOptions: { value: string; label: string }[] = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'converted', label: 'Converted' },
    { value: 'lost', label: 'Lost' },
    { value: 'archived', label: 'Archived' },
  ];

  const sourceOptions = [
    { value: 'autoscout24', label: 'AutoScout24' },
    { value: 'mobile-de', label: 'Mobile.de' },
    { value: 'ss-com', label: 'SS.com' },
    { value: 'ebay-motors', label: 'eBay Motors' },
    { value: 'facebook', label: 'Facebook Marketplace' },
    { value: 'manual', label: 'Manual' },
  ];

  const handleCreate = async (values: any) => {
    try {
      await createLead.mutateAsync({
        customer_name: values.customer_name,
        email: values.email,
        phone: values.phone,
        car_interest: values.car_interest,
        budget: values.budget,
        source: values.source || 'manual',
        status: 'new',
      });
      setCreateModalOpen(false);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleEdit = async (values: any) => {
    if (!selectedLead) return;
    try {
      await updateLead.mutateAsync({
        id: selectedLead.id,
        data: values,
      });
      setEditModalOpen(false);
      setSelectedLead(null);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleDelete = async () => {
    if (!selectedLead) return;
    try {
      await deleteLead.mutateAsync(selectedLead.id);
      setDeleteModalOpen(false);
      setSelectedLead(null);
    } catch (error) {
      // Error handled by mutation
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'new':
        return 'blue';
      case 'contacted':
        return 'cyan';
      case 'qualified':
        return 'green';
      case 'converted':
        return 'teal';
      case 'lost':
        return 'red';
      case 'archived':
        return 'gray';
      default:
        return 'gray';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatCurrency = (amount?: number) => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'EUR',
    }).format(amount);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={1}>Leads</Title>
            <Text c="dimmed" mt="xs">
              Manage and track your leads
            </Text>
          </div>
          <Group>
            <Menu>
              <Menu.Target>
                <Button variant="outline" leftSection={<IconDownload size={16} />}>
                  Import/Export
                </Button>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  leftSection={<IconUpload size={16} />}
                  onClick={() => setImportExportOpen(true)}
                >
                  Import Leads
                </Menu.Item>
                <Menu.Item leftSection={<IconDownload size={16} />}>
                  Export Leads
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateModalOpen(true)}>
              New Lead
            </Button>
          </Group>
        </Group>

        {/* Filters */}
        <Paper p="md" radius="md" withBorder>
          <Group>
            <TextInput
              placeholder="Search leads..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{ flex: 1 }}
            />
            <Select
              placeholder="Status"
              data={statusOptions}
              value={statusFilter}
              onChange={setStatusFilter}
              clearable
              leftSection={<IconFilter size={16} />}
            />
            <Select
              placeholder="Source"
              data={sourceOptions}
              value={sourceFilter}
              onChange={setSourceFilter}
              clearable
            />
            {(statusFilter || sourceFilter) && (
              <Button
                variant="subtle"
                onClick={() => {
                  setStatusFilter(null);
                  setSourceFilter(null);
                }}
              >
                Clear Filters
              </Button>
            )}
          </Group>
        </Paper>

        {/* Leads Table */}
        <Paper radius="md" withBorder>
          {isLoading ? (
            <Text ta="center" py="xl">
              Loading leads...
            </Text>
          ) : filteredLeads.length === 0 ? (
            <Text ta="center" py="xl" c="dimmed">
              No leads found
            </Text>
          ) : (
            <Table>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Customer</Table.Th>
                  <Table.Th>Contact</Table.Th>
                  <Table.Th>Car Interest</Table.Th>
                  <Table.Th>Budget</Table.Th>
                  <Table.Th>Source</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Score</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredLeads.map((lead: Lead) => (
                  <Table.Tr key={lead.id}>
                    <Table.Td>
                      <Text fw={500}>{lead.customer_name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Stack gap={2}>
                        {lead.email && <Text size="sm">{lead.email}</Text>}
                        {lead.phone && <Text size="sm" c="dimmed">{lead.phone}</Text>}
                      </Stack>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{lead.car_interest || 'N/A'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatCurrency(lead.budget)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light">{lead.source}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={getStatusColor(lead.status)}>{lead.status}</Badge>
                    </Table.Td>
                    <Table.Td>
                      <Badge color={lead.score >= 70 ? 'green' : lead.score >= 50 ? 'yellow' : 'red'}>
                        {lead.score}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{formatDate(lead.created_at)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        <Tooltip label="View Details">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => navigate(ROUTES.LEAD_DETAILS(lead.id))}
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Edit">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => {
                              setSelectedLead(lead);
                              setEditModalOpen(true);
                            }}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Delete">
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              setSelectedLead(lead);
                              setDeleteModalOpen(true);
                            }}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Paper>

        {/* Create Lead Modal */}
        <Modal
          opened={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          title="Create New Lead"
          size="lg"
        >
          <CreateLeadForm onSubmit={handleCreate} onCancel={() => setCreateModalOpen(false)} />
        </Modal>

        {/* Edit Lead Modal */}
        <Modal
          opened={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedLead(null);
          }}
          title="Edit Lead"
          size="lg"
        >
          {selectedLead && (
            <EditLeadForm
              lead={selectedLead}
              onSubmit={handleEdit}
              onCancel={() => {
                setEditModalOpen(false);
                setSelectedLead(null);
              }}
            />
          )}
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          opened={deleteModalOpen}
          onClose={() => {
            setDeleteModalOpen(false);
            setSelectedLead(null);
          }}
          title="Delete Lead"
        >
          <Stack gap="md">
            <Text>
              Are you sure you want to delete lead for <strong>{selectedLead?.customer_name}</strong>?
              This action cannot be undone.
            </Text>
            <Group justify="flex-end">
              <Button variant="subtle" onClick={() => {
                setDeleteModalOpen(false);
                setSelectedLead(null);
              }}>
                Cancel
              </Button>
              <Button color="red" onClick={handleDelete} loading={deleteLead.isPending}>
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Import/Export Modal */}
        <Modal
          opened={importExportOpen}
          onClose={() => setImportExportOpen(false)}
          title="Import/Export Leads"
          size="xl"
        >
          <LeadImportExport />
        </Modal>
      </Stack>
    </Container>
  );
}

// Create Lead Form Component
function CreateLeadForm({ onSubmit, onCancel }: { onSubmit: (values: any) => void; onCancel: () => void }) {
  const [formData, setFormData] = useState({
    customer_name: '',
    email: '',
    phone: '',
    car_interest: '',
    budget: undefined as number | undefined,
    source: 'manual',
  });

  return (
    <Stack gap="md">
      <TextInput
        label="Customer Name"
        required
        value={formData.customer_name}
        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
      />
      <TextInput
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
      />
      <TextInput
        label="Phone"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
      />
      <TextInput
        label="Car Interest"
        value={formData.car_interest}
        onChange={(e) => setFormData({ ...formData, car_interest: e.target.value })}
      />
      <NumberInput
        label="Budget"
        value={formData.budget}
        onChange={(value) => setFormData({ ...formData, budget: typeof value === 'number' ? value : undefined })}
        min={0}
        prefix="€"
      />
      <Select
        label="Source"
        data={[
          { value: 'manual', label: 'Manual' },
          { value: 'autoscout24', label: 'AutoScout24' },
          { value: 'mobile-de', label: 'Mobile.de' },
          { value: 'ss-com', label: 'SS.com' },
        ]}
        value={formData.source}
        onChange={(value) => setFormData({ ...formData, source: value || 'manual' })}
      />
      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(formData)}>
          Create Lead
        </Button>
      </Group>
    </Stack>
  );
}

// Edit Lead Form Component
function EditLeadForm({
  lead,
  onSubmit,
  onCancel,
}: {
  lead: Lead;
  onSubmit: (values: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    customer_name: lead.customer_name,
    email: lead.email || '',
    phone: lead.phone || '',
    car_interest: lead.car_interest || '',
    budget: lead.budget,
    status: lead.status,
  });

  const statusOptions = [
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'qualified', label: 'Qualified' },
    { value: 'converted', label: 'Converted' },
    { value: 'lost', label: 'Lost' },
    { value: 'archived', label: 'Archived' },
  ];

  return (
    <Stack gap="md">
      <TextInput
        label="Customer Name"
        required
        value={formData.customer_name}
        onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
      />
      <TextInput
        label="Email"
        type="email"
        value={formData.email}
        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
      />
      <TextInput
        label="Phone"
        value={formData.phone}
        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
      />
      <TextInput
        label="Car Interest"
        value={formData.car_interest}
        onChange={(e) => setFormData({ ...formData, car_interest: e.target.value })}
      />
      <NumberInput
        label="Budget"
        value={formData.budget}
        onChange={(value) => setFormData({ ...formData, budget: typeof value === 'number' ? value : undefined })}
        min={0}
        prefix="€"
      />
      <Select
        label="Status"
        data={statusOptions}
        value={formData.status}
        onChange={(value) => setFormData({ ...formData, status: value || 'new' })}
      />
      <Group justify="flex-end" mt="md">
        <Button variant="subtle" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={() => onSubmit(formData)}>
          Save Changes
        </Button>
      </Group>
    </Stack>
  );
}
